import { useState, useEffect } from 'react';
import { getAuthData } from '../../utils/sessionStorage';
import { fetchCaseDetails } from '../../services/api';

/**
 * CaseInfoDisplay - Shared component to display case information
 * Accepts case ID as prop or extracts from URL parameters and fetches/displays case details
 */
const CaseInfoDisplay = ({
  caseId = null,
  providerId = null,
  className = '',
  showPatientInfo = true,
  showProviderInfo = true,
  showSessionInfo = true,
  compact = false,
  onCaseLoaded = null
}) => {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Extract case ID from URL parameters if not provided as prop
  const getCaseIdFromUrl = () => {
    if (caseId) return caseId;
    
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('case_id') || urlParams.get('caseId') || urlParams.get('id');
  };

  // Fetch case data
  const fetchCaseData = async (caseId) => {
    if (!caseId) return;

    setLoading(true);
    setError(null);

    try {
      const authData = getAuthData();
      const apiToken = authData?.api_token;
      
      if (!apiToken) {
        setError('Authentication token not available');
        return;
      }

      // Use centralized API function
      const response = await fetchCaseDetails(apiToken, caseId, providerId);
      // Extract case data from the response
      const data = response.case || response;
      setCaseData(data);
      
      // Notify parent component if callback provided
      if (onCaseLoaded) {
        onCaseLoaded(data);
      }
    } catch (err) {
      console.error('Error fetching case data:', err);
      setError(err.message || 'Failed to load case information');
    } finally {
      setLoading(false);
    }
  };

  // Load case data when URL changes or caseId prop changes
  useEffect(() => {
    const currentCaseId = getCaseIdFromUrl();
    if (currentCaseId) {
      fetchCaseData(currentCaseId);
    } else {
      setCaseData(null);
      setError(null);
    }
  }, [caseId]); // Watch for caseId prop changes

  // Format date for display (matching AnkrCases)
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Get patient display name
  const getPatientDisplayName = () => {
    if (!caseData?.patient_info_fk) return 'Unknown Patient';
    const { first_name, last_name, middle_name } = caseData.patient_info_fk;
    return [first_name, middle_name, last_name].filter(Boolean).join(' ');
  };

  // Get provider display name
  const getProviderDisplayName = () => {
    if (!caseData?.provider_info_fk) return 'Unknown Provider';
    const { first_name, last_name, provider_type } = caseData.provider_info_fk;
    const name = [first_name, last_name].filter(Boolean).join(' ');
    return provider_type ? `${name}` : name;
  };

  // Get status badge class (matching AnkrCases)
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'initiated':
      case 'short_url_sent':
      case 'short_url_accessed':
      case 'yob_validated':
      case 'token_generated':
        return 'ankr-status-badge info';
      case 'active':
      case 'in_progress':
      case 'patient_checkin_initiated':
      case 'device_check_complete':
      case 'forms_started':
      case 'forms_filled':
      case 'forms_completed':
      case 'insurance_started':
      case 'insurance_verified':
        return 'ankr-status-badge active';
      case 'patient_declined_video':
      case 'patient_requested_human_support':
      case 'locked - attempts exceeded':
        return 'ankr-status-badge warning';
      case 'completed':
      case 'check_in_complete':
      case 'unlocked_by_admin':
        return 'ankr-status-badge completed';
      case 'cancelled':
      case 'expired':
        return 'ankr-status-badge cancelled';
      default:
        return 'ankr-status-badge default';
    }
  };

  // Handle phone call
  const handlePhoneCall = (phoneNumber) => {
    if (phoneNumber) {
      window.open(`tel:${phoneNumber}`);
    }
  };

  // Handle SMS
  const handleSMS = (phoneNumber) => {
    if (phoneNumber) {
      window.open(`sms:${phoneNumber}`);
    }
  };

  // Handle email
  const handleEmail = (email) => {
    if (email) {
      window.open(`mailto:${email}`);
    }
  };

  if (loading) {
    return (
      <div className={`card border d-flex align-items-center justify-content-center ${compact ? 'p-2' : 'p-3'} ${className}`} style={{ minHeight: '80px' }}>
        <div className="d-flex align-items-center text-muted">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span>Loading case information...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`card border d-flex align-items-center justify-content-center ${compact ? 'p-2' : 'p-3'} ${className}`} style={{ minHeight: '80px' }}>
        <div className="d-flex align-items-center text-danger">
          <span className="me-2">⚠️</span>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return null; // Don't render anything if no case data
  }

  return (
    <div className={`card border ${compact ? 'p-2 mb-2' : 'p-3 mb-3'} ${className}`}>
      {/* Case Header */}
      <div className="ankr-case-header">
        <div className="ankr-case-id">
          Case #{caseData.CoreServer_CaseID || caseData.id}
        </div>
        <div className={getStatusBadgeClass(caseData.mira_sol_case_status)}>
          {caseData.mira_sol_case_status?.replace(/_/g, ' ') || 'Unknown'}
        </div>
      </div>

      <div className="ankr-case-details">
        {showPatientInfo && caseData.patient_info_fk && (
          <>
            {/* Patient Name */}
            <div className="d-flex d-flex-inline">
              <strong>{getPatientDisplayName()}</strong>
            </div>

            {/* DOB & MRN */}
            <div className="d-flex d-flex-inline small text-muted">
              {caseData.patient_info_fk.dob && (
                <><b>DOB:&nbsp;</b> {new Date(caseData.patient_info_fk.dob).toLocaleDateString()} | &nbsp;</>
              )}
              <b>MRN:&nbsp;</b> {caseData.patient_info_fk.ehr_medical_record_number || 'Not found'}
            </div>
          </>
        )}

        {showProviderInfo && caseData.provider_info_fk && (
          <div className="d-flex d-flex-inline small text-muted">
            <b>Provider:&nbsp;</b> {getProviderDisplayName()}
            {caseData.provider_info_fk.specialties && caseData.provider_info_fk.specialties.length > 0 && (
              <> ({caseData.provider_info_fk.specialties[0].name})</>
            )}
          </div>
        )}

        {showSessionInfo && (
          <div className="d-flex d-flex-inline small text-muted">
            <b>Created:&nbsp;</b> {formatDate(caseData.created_at)}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseInfoDisplay;